import React, { useState } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { FiUploadCloud, FiFileText } from 'react-icons/fi';
import Modal from '../../components/ui/Modal';
import ErrorBanner from '../../components/ui/ErrorBanner';
import CustomerDetailDrawer from '../customers/CustomerDetailDrawer';
import { createInvoice } from '../../api/invoices';
import { getProductNamesAll } from '../../api/products';
import { extractErrorMessage } from '../../api/client';
import { toIsoDate } from '../../utils/date';
import InvoiceFormInner from './InvoiceFormInner';
import ExternalInvoiceForm from './ExternalInvoiceForm';
import { emptyItem, computeTotals } from './InvoiceItemsEditor';

// Mode toggle pill — switches between Regular and Upload modes
function ModeToggle({ mode, onChange }) {
  const Btn = ({ value, Icon, label }) => (
    <button
      type="button"
      onClick={() => onChange(value)}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors whitespace-nowrap ${mode === value
          ? 'bg-[#185FA5] text-white shadow-sm'
          : 'text-slate-500 hover:bg-slate-100'
        }`}
    >
      <Icon size={13} />
      {label}
    </button>
  );
  return (
    <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1 w-fit">
      <Btn value="regular" Icon={FiFileText} label="Create Invoice" />
      <Btn value="upload" Icon={FiUploadCloud} label="Upload Invoice" />
    </div>
  );
}

function defaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return toIsoDate(d);
}

export default function InvoiceForm({ open, onClose, onCreated }) {
  const [mode, setMode] = useState('regular'); // 'regular' | 'upload'
  const qc = useQueryClient();
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [form, setForm] = useState({
    customerId: null,
    productId: '',
    invoiceType: '',
    invoiceDate: toIsoDate(new Date()),
    dueDate: defaultDueDate(),
    notes: '',
    items: [emptyItem()],
    isGst: true,
    currency: 'INR',
    billingCountry: 'IN',
    poNumber: '',
    contactPerson: '',
    createdByName: '',
  });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  const reset = () => {
    setMode('regular');
    setForm({
      customerId: null,
      productId: '',
      invoiceType: '',
      invoiceDate: toIsoDate(new Date()),
      dueDate: defaultDueDate(),
      notes: '',
      items: [emptyItem()],
      isGst: true,
      currency: 'INR',
      billingCountry: 'IN',
      poNumber: '',
      contactPerson: '',
      createdByName: '',
    });
    setErrors({});
    setSubmitError(null);
    setShowCustomerModal(false);
  };

  const handleClose = () => {
    if (mutation.isLoading) return;
    reset();
    onClose?.();
  };

  const handleCustomerCreated = (newCustomer) => {
    if (newCustomer?.id) {
      setForm((f) => ({ ...f, customerId: newCustomer.id }));
      setErrors((e) => ({ ...e, customerId: null }));
    }
    setShowCustomerModal(false);
  };

  const mutation = useMutation({
    mutationFn: createInvoice,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['revenue'] });
      toast.success(`Invoice ${data?.invoiceNumber || ''} created`);
      onCreated?.(data);
      reset();
      onClose?.();
    },
    onError: (err) => {
      setSubmitError(err);
      toast.error(extractErrorMessage(err));
    },
  });

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'list-simple'],
    queryFn: getProductNamesAll,
  });

  const validate = () => {
    const errs = {};
    if (!form.customerId) errs.customerId = 'Customer is required';
    if (!form.dueDate) errs.dueDate = 'Due date is required';
    if (!form.productId) errs.productId = 'Product is required';
    if (!form.currency) errs.currency = 'Currency is required';
    if (!form.items.length) errs.items = 'Add at least one item';
    const itemErrs = {};
    form.items.forEach((it, i) => {
      const r = {};
      if (!it.name || !String(it.name).trim()) r.name = 'Required';
      const q = Number(it.quantity);
      if (!q || q <= 0) r.quantity = 'Must be > 0';
      const p = Number(it.unitPrice);
      if (Number.isNaN(p) || p < 0) r.unitPrice = 'Invalid';
      if (Object.keys(r).length) itemErrs[i] = r;
    });
    if (Object.keys(itemErrs).length) errs.itemRows = itemErrs;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;
    const payload = {
      customerId: Number(form.customerId),
      ...(form.productId !== '' && form.productId != null
        ? { productId: Number(form.productId) }
        : {}),
      ...(form.invoiceType ? { invoiceType: form.invoiceType } : {}),
      ...(form.invoiceDate ? { invoiceDate: form.invoiceDate } : {}),
      dueDate: form.dueDate,
      isGst: form.isGst,
      currency: form.currency || 'INR',
      billingCountry: form.billingCountry || 'IN',
      ...(form.notes ? { notes: form.notes } : {}),
      ...(form.poNumber ? { poNumber: form.poNumber } : {}),
      ...(form.contactPerson ? { contactPerson: form.contactPerson } : {}),
      ...(form.createdByName ? { createdByName: form.createdByName } : {}),
      items: form.items.map((it) => ({
        name: String(it.name || '').trim(),
        ...(it.description ? { description: it.description } : {}),
        quantity: Number(it.quantity) || 0,
        unitPrice: Number(it.unitPrice) || 0,
        taxRate: Number(it.taxRate) || 0,
      })),
    };
    mutation.mutate(payload);
  };

  const totals = React.useMemo(() => computeTotals(form.items, form.isGst), [form.items, form.isGst]);
  const formatCurrency = (val) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val);

  const isUpload = mode === 'upload';

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={isUpload ? 'Upload external invoice' : 'Create invoice'}
        description={
          isUpload
            ? 'Upload an invoice created outside the system.'
            : 'Save as draft or send immediately after creating.'
        }
        size="lg"
        headerAction={
          <ModeToggle
            mode={mode}
            onChange={(m) => { setMode(m); setSubmitError(null); setErrors({}); }}
          />
        }
        footer={
          <div className="flex w-full items-center justify-between">
            <div>
              {!isUpload && (
                <div className="flex items-baseline gap-1.5">
                  <span className="text-[12px] text-slate-500">Subtotal</span>
                  <span className="text-[15px] font-medium text-slate-900">{formatCurrency(totals.total)}</span>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClose}
                disabled={mutation.isLoading}
                className="h-[34px] px-[14px] border border-slate-300 rounded-md bg-transparent text-slate-500 text-[13px] cursor-pointer font-sans hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                form={isUpload ? ExternalInvoiceForm.formId : undefined}
                onClick={isUpload ? undefined : handleSubmit}
                disabled={mutation.isLoading}
                className="h-[34px] px-[16px] border-none rounded-md bg-[#185FA5] text-white text-[13px] font-medium cursor-pointer font-sans hover:bg-[#0C447C] transition-colors"
              >
                {isUpload ? 'Upload Invoice' : mutation.isLoading ? 'Creating...' : 'Create Invoice'}
              </button>
            </div>
          </div>
        }
      >
        {isUpload ? (
          <ExternalInvoiceForm
            onCreated={(data) => { onCreated?.(data); reset(); onClose?.(); }}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-0">
            {submitError && <div className="mb-4"><ErrorBanner error={submitError} title="Could not create invoice" /></div>}
            <InvoiceFormInner
              form={form}
              update={(patch) => setForm((f) => ({ ...f, ...patch }))}
              errors={errors}
              products={products}
              onNewCustomer={() => setShowCustomerModal(true)}
            />
          </form>
        )}
      </Modal>

      <CustomerDetailDrawer
        open={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSuccess={handleCustomerCreated}
      />
    </>
  );
}
